import React from 'react'
import { H2, H3, OL, LI, Paragraph, Strong, Underline } from 'components/Legal'

export const ExpertContractContent: React.FC<React.PWC<unknown>> = () => (
  <>
    <H2>THE EXPERT DESIGNER AGREEMENT</H2>
    <H3>Effective: February 1, 2024</H3>
    <br />
    <Paragraph>
      The Expert, Inc., a Delaware corporation (collectively, &ldquo;
      <Strong>The Expert</Strong>&rdquo;, &ldquo;<Strong>we</Strong>,&rdquo;
      &ldquo;
      <Strong>our</Strong>&rdquo; or &ldquo;<Strong>us</Strong>&rdquo;) owns and
      operates The Expert Service, which enables individuals to find and connect
      with interior designers registered as &ldquo;experts&rdquo; with The
      Expert Service to receive home design guidance, advice and support via
      video consultations and/or through designer pages, and to purchase certain
      home good products through the Marketplace. This Designer Agreement (this
      &ldquo;<Strong>Agreement</Strong>&rdquo;) is incorporated into and made
      part of The Expert&apos;s Terms of Service available at{' '}
      <a
        href="https://www.theexpert.com/terms"
        target="_blank"
        rel="noreferrer"
      >
        https://www.theexpert.com/terms
      </a>{' '}
      (the &ldquo;<Strong>Terms</Strong>&rdquo;). Capitalized terms use in this
      Agreement and not defined have the meanings given to them in the Terms. In
      the event of any conflict between the terms and conditions of this
      Agreement and the Terms, this Agreement shall control solely with respect
      the subject matter herein.
    </Paragraph>
    <Paragraph>
      <Strong>IMPORTANT -- PLEASE READ THIS AGREEMENT CAREFULLY</Strong>. This
      Agreement sets forth the legally binding terms between The Expert and you
      (hereinafter, &ldquo;<Strong>you</Strong>,&rdquo; and/or &ldquo;
      <Strong>your</Strong>
      &rdquo;), under which you may register as a designer (&ldquo;
      <Strong>Designer</Strong>&rdquo;) and offer your design consultations
      (&ldquo;
      <Strong>Consultations</Strong>&rdquo;) to end user clients of The Expert
      Service (the &ldquo;<Strong>Clients</Strong>&rdquo;).{' '}
      <Strong>
        PLEASE NOTE, THIS AGREEMENT ALSO CONTAIN AN AGREEMENT TO ARBITRATE IN
        SECTION 11 WHICH WILL REQUIRE YOU TO SUBMIT CLAIMS YOU HAVE AGAINST US
        TO BINDING AND FINAL ARBITRATION.
      </Strong>
    </Paragraph>
    <OL>
      <LI>
        <Strong>Acceptance of this Agreement</Strong>. By registering as a
        Designer or clicking &ldquo;I Accept&rdquo; you: (1) acknowledge that
        you have read, understand, and agree to be bound by this Agreement
        (including,{' '}
        <a
          href="https://www.theexpert.com/terms"
          target="_blank"
          rel="noreferrer"
        >
          The Expert Terms of Service
        </a>{' '}
        and all of the terms and conditions specified or referenced below); (2)
        affirm, represent and warrant that you are 18 years old or the age of
        majority in your jurisdiction and that you are fully able and competent
        to enter into the terms, conditions, obligations, affirmations,
        representations, and warranties set forth in this Agreement; and (3)
        acknowledge and agree that you are entering into this Agreement with The
        Expert, Inc., a Delaware corporation. If you are agreeing to this
        Agreement on behalf of another person, or an entity, organization, or
        company, who is the Designer, you represent and warrant that you have
        the authority to bind that person, entity, organization, and/or company
        to this Agreement and you agree to be bound by this Agreement on behalf
        of that person, entity, organization, and/or company (in which case,
        &ldquo;
        <Strong>you</Strong>&rdquo; and &ldquo;<Strong>your</Strong>&rdquo; as
        used throughout this Agreement shall also refer to such other person,
        entity, organization, and/or company).{' '}
        <Strong>
          IF YOU DO NOT AGREE WITH ALL OF THE TERMS AND CONDITIONS OF THIS
          AGREEMENT, OR YOU DO NOT MEET THE ELIGIBILITY REQUIREMENTS OR DO NOT
          HAVE THE REQUISITE AUTHORITY AS SET FORTH ABOVE, DO NOT ACCEPT THIS
          AGREEMENT.
        </Strong>
      </LI>
      <LI>
        <Strong>Your Consultations; Customer Service</Strong>
        <OL>
          <LI>
            <Underline>Consultations</Underline>. You agree to provide your
            Consultations (if applicable to you) in a professional and
            workmanlike manner and will comply with all laws, regulations, and
            ordinances (including, obtaining any required licenses, permits,
            authorizations and/or approvals) applicable to your performance of
            the Consultations. You will not engage in any activity or action
            that may damage the reputation of or disparage The Expert, The
            Expert Services, or any of The Expert&apos;s trademarks.
          </LI>
          <LI>
            <Underline>Interactions with Clients</Underline>. Please note that
            The Expert does not currently conduct any verification of or
            background checks on any Clients. Accordingly, we cannot and do not
            represent, warrant or guarantee the identity of any Clients and/or
            whether any Clients are trustworthy. You are solely responsible for
            making your own decisions about the suitability of Clients whom you
            agree to provide Consultations to, or otherwise contact,
            communicate, transact or interact with in connection with your use
            of The Expert Service as a Designer. In addition, you acknowledge,
            understand, and agree that, except solely with respect to our
            payment obligations to you with respect to Consultations, any
            agreement(s), understandings, interactions, communications, and/or
            dealings between you and any Client, including, but not limited to,
            the Consultations (all of the foregoing, collectively, &ldquo;
            <Strong>Dealings</Strong>&rdquo;), shall be solely between you and
            the applicable Client. The Expert shall not be a party to any
            Dealings, or have any responsibility or liability for such Dealings
            and/or the Client&apos;s performance of its obligations under or in
            connection with any such Dealings. You further acknowledge,
            understand and agree that any problems or disputes between you and
            any Client, including, but not limited to, with respect to any
            Dealings between you and any Client (collectively, &ldquo;
            <Strong>Your Disputes</Strong>&rdquo;) must be resolved solely
            between you and such Client. YOUR DEALINGS WITH ANY CLIENTS SHALL BE
            AT YOUR SOLE AND ENTIRE RISK. THE EXPERT IS NOT AND SHALL NOT BE
            RESPONSIBLE FOR ANY LOSS, HARM OR DAMAGE OF ANY SORT INCURRED AS A
            RESULT OF ANY SUCH DEALINGS OR OTHERWISE ARISING FROM YOUR DISPUTES
            AND YOU AGREE THAT THE EXPERT IS UNDER AND HAS NO OBLIGATION TO
            BECOME INVOLVED ANY OF YOUR DISPUTES.
          </LI>
          <LI>
            <Underline>Customer Service</Underline>. The Expert will serve as
            the point of contact for customer service for Clients. If any Client
            reaches out directly to you through The Expert Service regarding
            customer service issues, questions and/or requests regarding your
            Consultations, Designer page and/or Showroom, you agree to promptly
            forward the inquiry or communication to The Expert. In addition, you
            agree to provide any additional information and/or reasonable
            support and assistance, as reasonably requested by The Expert, in
            order to respond to the inquiry and/or communication.
          </LI>
        </OL>
      </LI>
      <LI>
        <Strong>Designer Materials & Obligations</Strong>
        <OL>
          <LI>
            <Underline>Designer Materials</Underline>. In support of the
            promotion and marketing of you as a Designer, and The Expert&apos;s
            creation and maintenance of your designated product showroom page as
            a Designer (the &ldquo;<Strong>Showroom</Strong>&rdquo;), and, if
            applicable, your Consultation offerings through The Expert Service,
            you agree to provide or make available to The Expert the materials,
            information and content requested by The Expert for the creation of
            a profile on The Expert Service for you as a Designer and your
            Showroom, including, without limitation, portfolio images of past
            design work, descriptions of your design qualification, the design
            services and/or design styles you offer, identification of your
            social media usernames and/or handles, and biographical information
            (collectively, the &ldquo;<Strong>Designer Materials</Strong>
            &rdquo;). You agree to provide complete and correct Designer
            Materials, and will maintain and keep such information up to date.
          </LI>
          <LI>
            <Underline>License to Designer Materials</Underline>. You hereby
            grant to The Expert a worldwide, perpetual, irrevocable (except as
            set forth in Section 3.3 below and Section 5 following termination),
            transferable, sublicensable, fully-paid, royalty-free license in and
            to your Designer Materials, and any name(s), voice(s), image(s),
            picture(s) or other likenesses of you or any other persons to the
            extent included in the Designer Materials (the &ldquo;
            <Strong>Likenesses</Strong>&rdquo;), to use, copy, reproduce,
            modify, perform, display, distribute, and disclose the Designer
            Materials and Likenesses: (a) on or through The Expert Service, The
            Expert&apos;s websites and/or The Expert&apos;s social media
            channels, to market, and promote you as a Designer, your
            Consultation, and The Expert Service to Clients and prospective
            clients and/or customers; and (b) in other promotional materials,
            communications, advertisements and/or marketing of The Expert, in
            any media whatsoever or distribution methods (whether now known,
            existing, or hereinafter developed), including, but not limited to,
            social media channels, to market, promote, and advertise The Expert
            and The Expert Service. The Expert shall attribute any use of the
            Designer Materials and/or Likeness to you unless otherwise approved
            by you.
          </LI>
          <LI>
            <Underline>Your Right to Revoke Certain Licenses</Underline>; Waiver
            of Rights. You may revoke the license grant set forth in Section
            3.2(b) by providing written notice to The Expert of such revocation,
            in which case, any further use of the Designer Materials and/or
            Likenesses for the purposes set forth in Section 3.2(b) above shall
            be subject to your prior approval. For the avoidance of doubt,
            following The Expert&apos;s receipt of your notice of revocation of
            such license grant, The Expert shall cease all further use of the
            Designer Materials and/or Likenesses, including, any further use in
            active marketing and/or ad campaigns, provided that, such revocation
            shall not require The Expert to, or to require any third party to,
            remove, retract or take down any marketing, advertising, promotion
            and/or communications distributed or published prior to such
            revocation. You also understand and agree that the Designer
            Materials and/or Likenesses may be combined with other images, text,
            graphics, film, audio, audio-visual works, and may be cropped,
            altered or otherwise modified. This includes the right to add
            captions or otherwise modify the Designer Materials and/or
            Likenesses to ensure accessibility. EXCEPT AS OTHERWISE EXPRESSLY
            SET FORTH ABOVE, YOU HEREBY WAIVE AND RELINQUISH ANY AND ALL MORAL
            RIGHTS THAT YOU MIGHT OTHERWISE HAVE OR ASSERT AGAINST THE EXPERT
            WITH RESPECT TO THE DESIGNER MATERIALS AND/OR LIKENESSES; INCLUDING,
            BUT NOT LIMITED TO, ANY RIGHT THAT YOU MAY HAVE TO INSPECT OR
            APPROVE ANY USE BY THE EXPERT OF THE DESIGNER MATERIALS AND/OR
            LIKENESSES. YOU ACKNOWLEDGE AND AGREE THAT THE EXPERT IF UNDER NO
            OBLIGATION TO ACTUALLY EXPLOIT THE DESIGNER MATERIALS AND/OR
            LIKENESSES AT ANY TIME.
          </LI>
          <LI>
            <Underline>Ownership Rights</Underline>; Confidentiality. Subject
            only to the licenses you grant to us in this Agreement, you retain
            all right, title, and interest, including, all intellectual property
            rights, in and to, the Designer Materials and Likenesses. Subject to
            your rights in and to your Designer Materials and Likeness, The
            Expert will retain all right, title, and interest in and to any in
            marketing materials, content, and/or documentation created by and/or
            on behalf The Expert hereunder. In addition, you acknowledge and
            agree that, unless otherwise expressly agreed to by The Expert in
            writing, The Expert will control the content, appearance, design,
            functionality and all other aspects of your Designer profile page
            and Showroom, and any modifications, redesign and/or removal
            thereof. You acknowledge that the terms of this Agreement and any
            non-public information regarding the business of The Expert, and its
            products, and services shall be confidential (&ldquo;
            <Strong>Confidential Information</Strong>&rdquo;) and you agree that
            you will not disclose Confidential Information to any third party.
          </LI>
          <LI>
            <Underline>Designer Promotion</Underline>. Subject to the terms of
            this Agreement, you may, but are under no obligation to, market and
            promote The Expert Service and your participation as a Designer of
            The Expert Service, including, your Showroom and/or your
            Consultation offerings you provide through The Expert Service (if
            applicable), to your clients and customers, and through your
            website(s) and/or any social media accounts owned and/or controlled
            by you. If you engage in any such marketing and/or promotion, you
            acknowledge and agree: (a) that you will use The Expert&apos;s
            trademarks, logos and tradenames (&ldquo;
            <Strong>The Expert Marks</Strong>
            &rdquo;) at all times in the marketing and promoting of your
            participation as a Designer, your Showroom and/or your Consultations
            and you will comply with The Expert&apos;s then-current trademark
            usage guidelines provided by us to you; and (b) that you will not
            associate The Expert Marks with any content or material, or include
            and content or materials in any social media posts, advertising,
            and/or other marketing content and/or materials, that, in The
            Expert&apos;s discretion, is unlawful in any manner, or which is
            otherwise harmful, threatening, defamatory, obscene, offensive,
            harassing, sexually explicit, violent, discriminatory, or otherwise
            objectionable in, or that reflects unfavorably on, disparages, or
            otherwise causes a decrease in the goodwill and reputation of The
            Expert Marks, The Expert Service or any other products or services.
            All goodwill arising out of the use of The Expert Marks shall be on
            behalf of and shall inure to the benefit of The Expert.
          </LI>
          <LI>
            <Underline>Representations and Warranties</Underline>. You represent
            and warrant: (a) that the Designer Materials are your original
            material; (b) that you own or control all rights in and to the
            Designer Materials and Likenesses, and have the necessary rights to
            grant the licenses and releases granted in this Agreement; (c) that
            the use and distribution of the Designer Materials and Likenesses as
            authorized by you herein will not constitute a violation of any law
            or infringe upon the copyright, trademark, intellectual property or
            moral rights of any third party; (d) your execution of this
            Agreement and performance hereunder will not constitute a breach of
            any other agreement; and (d) you will comply with applicable laws,
            rules, regulations and guidelines (including the Federal Trade
            Commission&apos;s Guides Concerning the Use of Endorsements and
            Testimonials in Advertising &ndash;{' '}
            <a
              href="https://www.ftc.gov/sites/default/files/attachments/press-releases/ftc-publishes-final-guides-governing-endorsements-testimonials/091005revisedendorsementguides.pdf"
              target="_blank"
              rel="noreferrer"
            >
              https://www.ftc.gov/sites/default/files/attachments/press-releases/ftc-publishes-final-guides-governing-endorsements-testimonials/091005revisedendorsementguides.pdf
            </a>
            ), as well as the rules, regulations, terms, conditions, and
            policies of each applicable digital and/or social media platform in
            connection with your marketing and/or promotion activities hereunder
            (if applicable). For the avoidance of doubt, you may not provide any
            Designer Materials (or any part thereof) that is owned, controlled
            or obtained from a third party, or that is copyrighted, protected by
            trade secret or otherwise subject to third party proprietary rights,
            including patent, privacy and publicity rights, unless you are the
            owner of such rights or you have valid and enforceable permission
            from the rightful owner to use the Designer Materials as
            contemplated hereunder.
          </LI>
        </OL>
      </LI>
      <LI>
        <Strong>Fees and Payment Terms</Strong>
        <OL>
          <LI>
            <Underline>Consultation Fees</Underline>. As the Designer, and
            subject to The Expert&apos;s then-current consultation pricing
            guidelines, rules and/or policies, you will set the applicable
            hourly rates you wish to charge for Consultations you offer to
            Clients through The Expert Services (the &ldquo;
            <Strong>Consultation Fees</Strong>&rdquo;). For each Consultation
            purchased by Clients, you shall receive an amount equal to the
            Consultation Fee for such Consultation minus the Service Fees
            (defined in Section 4.2 below). The Expert shall collect the
            Consultation Fees directly from each Client who purchased a
            Consultation. Within 20 (twenty) business days after the end of each
            calendar month during the term of this Agreement, The Expert will
            make payments of the fees due to you hereunder based on the
            Consultation Fees actually received by The Expert from Clients who
            received the Consultations in such calendar month.
          </LI>
          <LI>
            <Underline>The Expert Fees</Underline>. In consideration for your
            use of The Expert Service to provide Consultations, The Expert shall
            receive an amount equal to 26% of the gross Consultation Fees paid
            for each Consultation (the &ldquo;<Strong>Service Fees</Strong>
            &rdquo;). The Expert may change the Service Fees, the pricing model,
            and/or the payment terms upon ten (10) days prior notice; provided
            that, for the avoidance of doubt, any Consultation Fees paid prior
            to the applicable effective date of such change, shall be subject to
            the Service Fees in effect prior to such change.
          </LI>
          <LI>
            <Underline>Showroom Commissions</Underline>. In addition, if you
            have entered into an addendum referencing this Agreement with The
            Expert with respect to Products purchased through your Showroom
            (hereinafter, the &ldquo;<Strong>Showroom Addendum</Strong>&rdquo;),
            you will be eligible to receive commissions on the sale of such
            Products pursuant to the terms and conditions of the Showroom
            Addendum (the &ldquo;
            <Strong>Commissions</Strong>&rdquo;). The Showroom Addendum (if
            applicable to you) is hereby incorporated into and made a part of
            this Agreement as if set forth in full, and the term
            &ldquo;Agreement&rdquo; as used herein and all other instruments and
            agreements executed or entered under this Agreement, shall, for all
            purposes, refer to this Agreement, together with, and as
            supplemented by, the Showroom Addendum for as long as the Showroom
            Addendum is in effect. In the event of any conflict between the
            terms of the Showroom Addendum and this Agreement, the Showroom
            Addendum will control, solely with respect to the subject matter
            thereof.
          </LI>
          <LI>
            <Underline>Payment Terms</Underline>. Payments of amounts due to you
            under this Agreement will be made electronically via The
            Expert&apos;s third-party payment provider (currently, Stripe
            (subject to change)). You are required to have all necessary
            accounts to receive and accept payments of any amounts due and
            payable by The Expert to you under this Agreement. In addition, you
            agree to provide any identifying information or tax documentation
            (such as a W-9 or W-8) requested by The Expert necessary for payment
            of amounts due, and you agree that we have the right to withhold
            appropriate taxes from your payments. The Expert reserves the right
            to withhold payments or impose other penalties if we do not receive
            proper identifying information or tax documentation from you. You
            understand and agree that you are ultimately responsible for any
            taxes on your income. You will be responsible for all costs expended
            by you and The Expert will be responsible for all costs expended by
            The Expert in connection with this Agreement. Please note that there
            will be deducted from any sums due to you hereunder an amount equal
            to the Consultation Fees and/or Commissions (as applicable) paid to
            you by The Expert where all, or substantially all, of the
            Consultations Fees and/or amounts paid by Clients for the purchase
            of Products through your Showroom have since been returned to the
            Client.
          </LI>
        </OL>
      </LI>
      <LI>
        <Strong>Term; Termination</Strong>. This Agreement will remain in full
        force and effect until terminated in accordance with the provisions of
        this Agreement. Either party may terminate this Agreement upon written
        notice to the other. For termination notices from you to The Expert,
        please notify us at{' '}
        <a href="mailto:hello@theexpert.com" target="_blank" rel="noreferrer">
          hello@theexpert.com
        </a>
        ; subject line: &ldquo;
        <em>Notice of Termination &ndash; Designer Agreement.</em>&rdquo;
        Notices to you will be sent to the email address you used when you first
        registered as a Designer. Notwithstanding the foregoing, in addition to
        any of The Expert&apos;s other rights or remedies, The Expert may
        immediately and without notice to you, terminate this Agreement if The
        Expert, in good faith, believes that you, your Designer Materials,
        and/or Consultations are in violation, or have violated, this Agreement,
        including any incorporated policies, guidelines, terms or rules. Upon
        termination, the licenses granted by you to us in Section 3.2 shall
        terminate; provided that, The Expert shall not be required to, nor
        require any third party to, remove, retract, or take down any Designer
        Materials and/or Likenesses or related marketing, advertising, promotion
        and/or communications distributed or published prior to the effective
        date of termination except where The Expert has violated the scope of
        the license grants. The following Sections will survive any expiration
        or termination of this Agreement: 1, 2, 3.2, 3.3, 3.4, 3.6, 6, 7, 8, 9,
        10, 11 and 13.
      </LI>
      <LI>
        <Strong>Indemnity</Strong>. You agree to indemnify, defend and hold
        harmless The Expert, its officers, employees, agents, directors,
        subsidiaries, affiliates, successors, assigns, suppliers, or licensors
        from any and all claims, actions, suits, demands, damages, liabilities,
        losses, costs and expenses (including attorneys&apos; reasonable fees)
        arising from or related to: (a) your violation of this Agreement
        (including, without limitation, any breach of your obligations,
        representations and warranties made in this Agreement), or violation of
        any of your agreements with any third party related to the Designer
        Materials; (b) any actual or alleged infringement, violation, or
        misappropriation of any intellectual property right or other proprietary
        right, including without limitation, any copyright, property, moral or
        privacy right, of any third party in connection with your Designer
        Materials, or use thereof by The Expert; and/or (c) your Consultations.
        The Expert will have sole control of the defense and any related
        settlement negotiations of such claims. You may participate and retain
        your own counsel at your expense.
      </LI>
      <LI>
        <Strong>Disclaimer</Strong>. THE EXPERT MAKES NO WARRANTIES UNDER THIS
        AGREEMENT AND HEREBY EXPRESSLY DISCLAIMs ANY REPRESENTATIONS, WARRANTIES
        OR GUARANTEES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF
        MERCHANTABILITY, FITNESS FOR ANY PARTICULAR PURPOSE, SATISFACTORY
        PURPOSE, ACCURACY, OR NON-INFRINGEMENT. THE EXPERT MAKES, AND HAS MADE,
        NO REPRESENTATIONS, WARRANTIES, OR GUARANTEES THAT CLIENTS WILL PURCHASE
        CONSULTATIONS OR ANY PRODUCTS, OR THAT THE EXPERT WILL SEEK, OBTAIN OR
        PROCURE ENGAGEMENTS OR CONSULTATIONS FOR YOU.
      </LI>
      <LI>
        <Strong>Limitation of Liability</Strong>. IN NO EVENT WILL THE EXPERT BE
        LIABLE TO YOU OR ANY THIRD PARTY OR PERSON FOR ANY LOST PROFITS, LOST
        REVENUES OR LOSS OF BUSINESS ADVANTAGE, COST OF COVER, OR ANY INDIRECT,
        SPECIAL, INCIDENTAL OR CONSEQUENTIAL DAMAGES ARISING OUT OF OR RELATED
        TO THIS AGREEMENT, WHETHER BASED ON BREACH OF CONTRACT, TORT (INCLUDING
        NEGLIGENCE) OR OTHERWISE, AND WHETHER OR NOT YOU HAVE BEEN ADVISED OF
        THE POSSIBILITY OF SUCH DAMAGE. THE EXPERT&apos;S TOTAL AGGREGATE
        LIABILITY TO YOU FOR ANY DAMAGES OR ALLEGED DAMAGES ARISING FROM OR
        RELATED TO THIS AGREEMENT, WHETHER IN CONTRACT, TORT OR ANY OTHER LEGAL
        THEORY, SHALL NOT EXCEED THE GREATER OF THE FEES PAID OR PAYABLE TO YOU
        BY THE EXPERT UNDER THIS AGREEMENT IN THE TWELVE (12) MONTH PERIOD
        PRECEDING THE CLAIM GIVING RISE TO THE LIABILITY AND $500. THE
        LIMITATIONS SPECIFIED IN THIS PARAGRAPH SHALL SURVIVE AND APPLY EVEN IF
        ANY LIMITED REMEDY SPECIFIED IN THIS AGREEMENT IS FOUND TO HAVE FAILED
        OF ITS ESSENTIAL PURPOSE.
      </LI>
      <LI>
        <Strong>Release</Strong>. YOU, ON BEHALF OF YOURSELF AND YOUR
        SUCCESSORS, ASSIGNS, REPRESENTATIVES, EMPLOYEES, AGENTS, CLIENTS, AND
        ANYONE ELSE CLAIMING BY OR THROUGH YOU (COLLECTIVELY, THE &ldquo;
        <Strong>RELEASING PARTIES</Strong>&rdquo;), HEREBY RELEASE, DISCHARGE
        AND HOLD HARMLESS THE EXPERT AND ITS DIRECTORS, OFFICERS, EMPLOYEES,
        CONTRACTORS, AGENTS, SUCCESSORS AND ASSIGNS, (COLLECTIVELY, THE &ldquo;
        <Strong>RELEASED PARTIES</Strong>&rdquo;), FROM AND HEREBY WAIVE AND
        RELINQUISH, EACH AND EVERY PAST, PRESENT AND FUTURE DISPUTE, CLAIM,
        CONTROVERSY, DEMAND, RIGHT, OBLIGATION, LIABILITY, ACTION AND CAUSE OF
        ACTION, OF EVERY KIND AND NATURE (INCLUDING, PERSONAL INJURY, DEATH,
        PROPERTY DAMAGE, LIBEL, DEFAMATION, INVASION OF PRIVACY, RIGHT OF
        PUBLICITY, OR INFRINGEMENT OF COPYRIGHT OR ANY OTHER INTELLECTUAL
        PROPERTY RIGHT), THAT HAS ARISEN OR ARISES OUT OF, OR OTHERWISE RELATES
        TO, DIRECTLY OR INDIRECTLY, THIS AGREEMENT, ANY DEALINGS AND/OR YOUR
        DISPUTES WITH CLIENTS, ANY UTILIZATION OF THE DESIGNER MATERIALS AND/OR
        LIKENESSES (INCLUDING, WITHOUT LIMITATION, ANY BLURRING, DISTORTION,
        ALTERATION, OPTICAL ILLUSION, OR USE IN COMPOSITE FORM, WHETHER
        INTENTIONAL OR OTHERWISE, THAT MAY OCCUR IN THE USE OR PUBLICATION OF
        THE DESIGNER MATERIALS AND/OR LIKENESSES), OR BASED UPON ANY FAILURE OR
        OMISSION TO MAKE USE OF THE DESIGNER MATERIALS AND/OR LIKENESSES. YOU
        HEREBY WAIVE CALIFORNIA CIVIL CODE SECTION 1542 (AND ANY LAW OR LEGAL
        PRINCIPLE OF SIMILAR EFFECT IN ANY JURISDICTION WORLDWIDE) IN CONNECTION
        WITH THE FOREGOING, WHICH STATES: &ldquo;A GENERAL RELEASE DOES NOT
        EXTEND TO CLAIMS WHICH A RELEASING PARTY DOES NOT KNOW OR SUSPECT TO
        EXIST IN THE RELEASING PARTY&apos;S FAVOR AT THE TIME OF EXECUTING THE
        RELEASE, WHICH IF KNOWN BY THE RELEASING PARTY MUST HAVE MATERIALLY
        AFFECTED THE RELEASING PARTY&apos;S SETTLEMENT WITH THE RELEASED
        PARTIES.&rdquo; YOU ACKNOWLEDGE AND AGREE THAT THE WAIVER AND RELEASE IS
        BINDING UPON THE HEIRS AND ASSIGNS OF THE RELEASING PARTIES AND IS
        IRREVOCABLE.
      </LI>
      <LI>
        <Strong>Electronic Communications</Strong>. You consent to receiving
        electronic communications from The Expert, including, without
        limitation, notices posted on our website and/or via email. These
        electronic communications may include notices about applicable fees and
        charges, transactional information and other information concerning or
        related to your Consultations, Consultations Fees and/or Commissions,
        and/or our use of the Designer Materials. These electronic
        communications are part of your relationship with The Expert and you
        receive them as part of your registration as a Designer. You agree that
        any notices, agreements, disclosures or other communications that we
        send to you electronically will satisfy any legal communication
        requirements, including that such communications be in writing.
      </LI>
      <LI>
        <Strong>Governing Law; Dispute Resolution</Strong>
        <OL>
          <LI>
            <Underline>Governing Law; Dispute Resolution</Underline>. This
            Agreement shall be governed in all respects by and construed in
            accordance with the laws of the State of New York, without regard to
            its conflicts of laws principles. Any controversy or claim arising
            out of or relating to this Agreement, or any breach thereof, shall
            be determined and settled by arbitration in Manhattan, New York,
            pursuant to the rules then pertaining of the American Arbitration
            Association as such rules are then prevailing, provided that the
            arbitrator and the parties shall comply with the following: (a) the
            arbitration shall be conducted by telephone, online and/or be solely
            based on written submissions, the specific manner shall be chosen by
            the party initiating the arbitration; (b) the arbitration shall not
            involve any personal appearance by the parties or witnesses unless
            otherwise mutually agreed by the parties; and (c) any judgment on
            the award rendered by the arbitrator shall be binding, final, and
            confidential, and may be entered in any court of competent
            jurisdiction. You and The Expert will share equally the
            administrative costs of such arbitration proceedings.
          </LI>
          <LI>
            <Underline>Venue; Exceptions to Arbitration</Underline>. If the
            agreement to arbitrate is found not to apply your or our claim, you
            and The Expert agree that any judicial proceeding will be brought in
            the federal or state courts of New York, New York. Both you and The
            Expert consent to venue and personal jurisdiction there.
            Notwithstanding anything in this Agreement to the contrary to the
            extent you have in any manner violated or threatened to violate any
            of The Expert&apos;s intellectual property rights, The Expert may
            seek injunctive or other appropriate relief in any state or federal
            court with competent jurisdiction in any country, including in the
            State of New York, United States of America, without first engaging
            in arbitration or the informal dispute process set forth in this
            Section, and you hereby consent to the personal jurisdiction and
            exclusive venue in such courts.
          </LI>
        </OL>
      </LI>
      <LI>
        <Strong>Modifications to this Agreement</Strong>. The Expert reserves
        the right to update or modify this Agreement at any time. Except as
        otherwise set forth in this Agreement or as stated below with respect to
        material changes, all updates and modifications to this Agreement will
        be effective from the day they are posted online at{' '}
        <a
          href="https://www.theexpert.com/blog/expert-designer-agreement"
          target="_blank"
          rel="noreferrer"
        >
          https://www.theexpert.com/blog/expert-designer-agreement
        </a>
        . If we make any material changes to this Agreement, we will provide you
        reasonable prior notice of such changes by sending a notification to the
        email address we have on file for you. Material changes to this
        Agreement will become effective on the date set forth in the notice. It
        is your responsibility to regularly visit and review this Agreement for
        updates, changes and modification. If you do not agree to any updates or
        modifications to this Agreement, simply terminate this Agreement as set
        forth in Section 5 and do not accept any Consultation bookings from
        Clients. If you do not termination this Agreement prior to the effective
        date of the revised Agreement, or accept bookings for Consultations
        after the effective date of the revised Agreement, that will constitute
        your acceptance of the revised Agreement.
      </LI>
      <LI>
        <Strong>General</Strong>. Your relationship with The Expert is that of
        an independent contractor and nothing in this Agreement is intended to,
        or shall be construed to, create a joint venture, partnership, agency,
        employment, or similar relationship between the parties. Neither the
        rights nor the obligations arising under this Agreement are assignable
        by you, and any such attempted assignment or transfer shall be void and
        without effect. The Expert may freely assign this Agreement and/or any
        of its rights and obligations under this Agreement. Any waiver of any
        provision of this Agreement must be in writing and executed by both
        parties. The failure of either party to exercise any right provided for
        by this Agreement shall not be deemed a waiver of that right. Except as
        otherwise specified in this Agreement, this Agreement may be amended or
        supplemented only by a writing that refers explicitly to this Agreement
        and that is signed on behalf of both parties. If any term or provision
        of this Agreement is determined to be illegal, unenforceable, or invalid
        in whole or in part for any reason, that provision shall be stricken
        from this Agreement and shall not affect the legality, enforceability or
        validity of the remainder of the provisions set forth in this Agreement.
        This Agreement is not intended to grant rights to anyone except you and
        The Expert, and this Agreement shall not create any third party
        beneficiary rights. This Agreement, and any other policies or terms and
        conditions referenced herein, constitutes the entire agreement between
        you and The Expert, and supersedes all prior oral or written agreements
        or communications, with regard to the subject matter described herein.
        Any notice to you may be provided by email. The headings of Sections of
        this Agreement are for convenience and are not to be used in
        interpreting this Agreement. As used in this Agreement, the word
        &ldquo;including&rdquo; means &ldquo;including but not limited
        to.&rdquo; You agree that this Agreement will not be construed against
        The Expert by having drafted it. The official text of this Agreement
        (and any notice submitted hereunder) will be in English. The parties
        acknowledge that they require that this Agreement be drawn up in the
        English language only. In the event of any dispute concerning the
        construction or meaning of this Agreement, reference will be made only
        to this Agreement as written in English and not to any translation into
        another language.
      </LI>
    </OL>
    <Paragraph>
      <Strong>QUESTIONS AND ADDITIONAL INFORMATION</Strong>. Please feel free to
      contact us at{' '}
      <a href="mailto:hello@theexpert.com" target="_blank" rel="noreferrer">
        hello@theexpert.com
      </a>{' '}
      if you have any questions about this Agreement.
    </Paragraph>
  </>
)
